import React, { useContext, useEffect, useState } from 'react'
import { Button, Divider, Spinner, Textarea, makeStyles, tokens } from '@fluentui/react-components'
import { Send24Regular } from '@fluentui/react-icons'
import { Text } from '../common/text/Text'
import { Avatar } from '../common/avatar/Avatar'
import { ActivityComment } from '../../api/schemas/schema'
import { api } from '../../helpers/apiHelper'
import { getFullName } from '../../helpers/accountHelper'
import { formatDate } from '../../helpers/stringHelper'
import { handleMessage } from '../../helpers/stateHelper'
import { isSystemAdmin } from '../../helpers/permissionHelper'
import { DataContext } from '../../context/DataContext'
import { IDataContext } from '../../interfaces/IDataContext'
import { t } from '../../i18n/i18n'
import { MoreOptionsMenuButton } from '../common/other/MoreOptionsMenuButton'

const useStyles = makeStyles({
   container: {
      marginTop: tokens.spacingVerticalL,
   },
   title: {
      marginBottom: tokens.spacingVerticalS,
   },
   addRow: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: tokens.spacingHorizontalS,
      marginBottom: tokens.spacingVerticalM,
   },
   textarea: {
      flex: 1,
   },
   entry: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: tokens.spacingHorizontalS,
      padding: `${tokens.spacingVerticalS} 0`,
   },
   entryContent: {
      flex: 1,
   },
   entryHeader: {
      display: 'flex',
      alignItems: 'baseline',
      gap: tokens.spacingHorizontalS,
   },
   empty: {
      color: tokens.colorNeutralForeground3,
   },
   placeholder: {
      display: 'flex',
      alignItems: 'center',
      minHeight: '20px',
   },
})

type CommentThreadProps = {
   activityId: string
   readOnly?: boolean
}
export const CommentThread = ({ activityId, readOnly }: CommentThreadProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { userAccount } = dataContext.state
   const [comments, setComments] = useState<ActivityComment[]>(undefined)
   const [newComment, setNewComment] = useState('')
   const [submitting, setSubmitting] = useState(false)

   useEffect(() => {
      const ac = new AbortController()
      api()
         .getActivityComments(activityId, { signal: ac.signal })
         .then((res) => setComments(res.data))
         .catch((error: any) => {
            if (error?.name === 'AbortError') return
            setComments([])
            handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('Comments') }))
         })
      return () => ac.abort()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [activityId])

   const onAddComment = async () => {
      if (!newComment.trim()) return
      setSubmitting(true)
      try {
         const added = (await api().addActivityComment(activityId, { text: newComment.trim() })).data
         setComments((prev) => [added, ...(prev ?? [])])
         setNewComment('')
      } catch (error) {
         handleMessage(dataContext.setRootState, 'error', t('CouldNotCreateItem', { Item: t('Comment') }))
      } finally {
         setSubmitting(false)
      }
   }

   const deleteComment = async (commentId: string, ctx: IDataContext, setState: React.Dispatch<React.SetStateAction<any>>) => {
      try {
         // The generated client takes commentId first - the API binds only {commentId} from the route,
         // so activityId is just a path segment. Keep the argument order matching schema.ts.
         await api().deleteActivityComment(commentId, activityId)
         setState((prev: ActivityComment[]) => (prev ?? []).filter((c) => c.id !== commentId))
      } catch {
         handleMessage(ctx.setRootState, 'error', t('CouldNotDeleteItem', { Item: t('Comment') }))
      }
   }

   return (
      <div className={classes.container}>
         <Divider appearance="brand" />
         <Text weight="semibold" className={classes.title}>
            {t('Comments')}
         </Text>
         {!readOnly && (
            <div className={classes.addRow}>
               <Textarea
                  className={classes.textarea}
                  placeholder={t('AddComment')}
                  value={newComment}
                  onChange={(_, data) => setNewComment(data.value)}
                  resize="vertical"
                  rows={2}
               />
               <Button appearance="primary" icon={<Send24Regular />} disabled={!newComment.trim() || submitting} onClick={onAddComment} />
            </div>
         )}
         {comments === undefined ? (
            <div className={classes.placeholder}>
               <Spinner size="tiny" />
            </div>
         ) : comments.length === 0 ? (
            <div className={classes.placeholder}>
               <Text className={classes.empty}>{t('NoComments')}</Text>
            </div>
         ) : (
            comments.map((comment, index) => (
               <React.Fragment key={comment.id}>
                  <div className={classes.entry}>
                     <Avatar onlyCircle name={getFullName(comment.createdBy)} />
                     <div className={classes.entryContent}>
                        <div className={classes.entryHeader}>
                           <Text weight="medium" noMargin>
                              {getFullName(comment.createdBy)}
                           </Text>
                           <Text size={200} noMargin>
                              {formatDate(comment.created, true)}
                           </Text>
                        </div>
                        <Text noMargin>{comment.text}</Text>
                     </div>
                     {!readOnly && (comment.createdById === userAccount.id || isSystemAdmin(userAccount)) && (
                        <MoreOptionsMenuButton
                           item={comment}
                           setState={setComments}
                           deleteFunction={deleteComment}
                           deleteMessage={t('DeleteItem', { Item: t('Comment').toLowerCase() })}
                           deleteConfirmationMessage={t('DeleteItemConfirmationMessage', { Item: t('Comment').toLowerCase() })}
                        />
                     )}
                  </div>
                  {index < comments.length - 1 && <Divider />}
               </React.Fragment>
            ))
         )}
      </div>
   )
}
